import { useState } from 'react';
import toast from 'react-hot-toast';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../api/apikeys.ts';
import { Button, Loading, Badge } from '../../components/ui.tsx';
import { CopyField } from './CopyField.tsx';
import { formatRelative } from '../../lib/format.ts';
import { ApiError } from '../../api/client.ts';

/** Personal API keys for the MCP server (docs/05). Secret shown exactly once. */
export function ApiKeysSection() {
  const keys = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();
  const [name, setName] = useState('');
  const [newSecret, setNewSecret] = useState<string | null>(null);

  function submit() {
    if (!name.trim()) return;
    create.mutate(name.trim(), {
      onSuccess: (res) => {
        setNewSecret(res.secret);
        setName('');
      },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
    });
  }

  return (
    <div className="card card-pad">
      <div className="card-title">Personal API keys</div>
      <p className="text-dim mb-4">
        Use these to drive your signage from Claude Desktop or scripts via the MCP server. Keys are
        scoped to your current organization.
      </p>

      {newSecret && (
        <div className="hint-box info mb-4">
          <strong>Copy your new key now</strong> — you won&apos;t be able to see it again.
          <div className="mt-2">
            <CopyField value={newSecret} label="New API key" />
          </div>
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setNewSecret(null)}>
            Done
          </Button>
        </div>
      )}

      <form
        className="row mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          aria-label="New key name"
          placeholder="Key name (e.g. Claude Desktop)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button variant="primary" type="submit" disabled={!name.trim() || create.isPending}>
          Create key
        </Button>
      </form>

      {keys.isLoading ? (
        <Loading />
      ) : (keys.data?.items ?? []).length === 0 ? (
        <p className="text-dim">No API keys yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.data!.items.map((k) => (
                <tr key={k.id}>
                  <td className="cell-strong">{k.name}</td>
                  <td>
                    <span className="mono">{k.prefix}…</span>
                  </td>
                  <td className="cell-dim">{k.last_used_at ? formatRelative(k.last_used_at) : <Badge tone="neutral">never</Badge>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        revoke.mutate(k.id, {
                          onSuccess: () => toast.success('Key revoked'),
                          onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
                        })
                      }
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
